// A floating LTR ⇄ RTL switch for /test-components.
//
// WHY: WhatsApp Web runs right-to-left for our Hebrew/Arabic users and that is
// where most of our layout bugs live, but reproducing them normally means
// switching the whole account's language. This flips the app on the spot so a
// component can be eyeballed both ways in seconds.
//
// HOW — and this is the part that matters: it flips the i18n LANGUAGE, not the
// `dir` attribute. Direction in this app is language-derived, and there are two
// independent consumers:
//   1. `applyDocumentDirection`, called from the production `languageChanged`
//      listener, sets html/body `dir` + the `rtl-mode` body class. That is what
//      `BTThemeProvider` observes to flip the MUI theme direction.
//   2. Components that ask i18n directly — the scheduler popup computes
//      `i18n.dir(i18n.language)` and stamps `direction` inline on its dialog.
// Setting `dir` by hand only satisfies (1): the scheduler popup stayed LTR on a
// fully RTL page, which is exactly the component we most want to inspect.
// Changing the language satisfies both, and reproduces what a real Hebrew user
// sees rather than an approximation of it. Side effect, deliberate: the UI is
// also translated.
//
// Scope: language is cached in localStorage by i18next, nothing is written to
// the account. Flipping back restores whatever language the page started in.
//
// KNOWN LIMIT (identical in production): this does not mirror emotion styles.
// Components written with hard-coded `left` / `marginRight` / `textAlign:'left'`
// stay put instead of flipping — that is precisely the class of bug this toggle
// exists to expose, not something it papers over.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isRtlLanguage } from '@common-services/rtl';

const RTL_TEST_LANGUAGE = 'he';
const LTR_FALLBACK_LANGUAGE = 'en';

export const DirectionToggle = () => {
  const { i18n } = useTranslation();
  const isRtl = isRtlLanguage(i18n.language);

  // The language the page was opened in, so flipping back lands where we started
  // instead of hard-coding everyone to English.
  const initialLanguage = useRef(i18n.language);
  const [, forceRender] = useState(0);

  useEffect(() => {
    const rerender = () => forceRender((n) => n + 1);
    i18n.on('languageChanged', rerender);
    return () => { i18n.off('languageChanged', rerender); };
  }, [i18n]);

  const flip = useCallback(() => {
    if (isRtl) {
      const back = isRtlLanguage(initialLanguage.current)
        ? LTR_FALLBACK_LANGUAGE
        : initialLanguage.current;
      i18n.changeLanguage(back);
    } else {
      i18n.changeLanguage(RTL_TEST_LANGUAGE);
    }
  }, [i18n, isRtl]);

  return (
    <button
      type="button"
      onClick={flip}
      aria-label={`Page direction: ${isRtl ? 'rtl' : 'ltr'}. Click to switch.`}
      title="Flip the whole app between LTR and RTL (switches the i18n language)"
      style={{
        position: 'fixed',
        bottom: 16,
        left: 16,
        // Above the scheduler dialog (MUI modals sit at 1300, the app's own
        // overlay at 9000) so direction can be flipped while a popup is open —
        // that is the main thing worth testing.
        zIndex: 99999,
        // Radix sets `pointer-events: none` on <body> while a modal dialog is
        // open, and that inherits down to us — the button rendered fine but was
        // simply not hit-testable, which reads exactly like a z-index problem
        // and is not one. Opt back in so the flip works with a dialog open.
        pointerEvents: 'auto',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 14px',
        borderRadius: 999,
        border: '1px solid rgba(0,0,0,.12)',
        background: isRtl ? '#0f5132' : '#1f2937',
        color: '#fff',
        fontSize: 13,
        fontWeight: 700,
        fontFamily: 'system-ui, sans-serif',
        cursor: 'pointer',
        boxShadow: '0 4px 14px rgba(0,0,0,.25)',
        // The control itself never flips — it must stay findable in both modes.
        direction: 'ltr',
      }}
    >
      <span style={{ fontSize: 15 }}>⇄</span>
      <span>{isRtl ? 'RTL' : 'LTR'}</span>
      <span style={{ opacity: 0.65, fontWeight: 500 }}>
        · switch to {isRtl ? 'LTR' : 'RTL'}
      </span>
    </button>
  );
};
